import type {
  ResolveWidgetVehicleResponse,
  WidgetBootstrapResponse,
} from '../types/PublicBookingChannelTypes'
import { buildJobTypes, matchJobIcon } from './jobTypes'

/**
 * An electric car at a garage with no EV technician (ACA-125). The server quotes
 * only what the garage can still do for it; this line says which of those a
 * customer would look for, worked out from the quotes rather than assumed.
 */

const NO_EV_WORK = "This garage doesn't currently work on electric vehicles."
const NO_EV_SERVICING = "This garage doesn't service electric vehicles"

type NamedKind = 'mot' | 'tyres' | 'brakes'

const NAMES: Record<NamedKind, string> = { mot: 'MOT', tyres: 'tyres', brakes: 'brakes' }
const ORDER: NamedKind[] = ['mot', 'tyres', 'brakes']

/** MOT and tyres as the tiles read them; brakes also by "brake" anywhere, as the server does. */
function kindOf(text: string | null | undefined): NamedKind | null {
  const icon = matchJobIcon(text ?? '')
  if (icon === 'mot' || icon === 'tyres' || icon === 'brakes') return icon
  return /brake/i.test(text ?? '') ? 'brakes' : null
}

function listed(kinds: NamedKind[]): string {
  const names = kinds.map((kind) => NAMES[kind])
  const verb = kinds.length === 1 && kinds[0] === 'mot' ? 'is' : 'are'
  const list =
    names.length === 1 ? names[0] : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
  return `${list} ${verb} available.`
}

/**
 * The notice for an electric car the garage doesn't service, or null when the
 * lookup doesn't say so. Names MOT, tyres and brakes when a quoted service the
 * customer can book covers them; any other quoted service counts as offered but
 * goes unnamed.
 */
export function evServicesMessage(
  vehicle: ResolveWidgetVehicleResponse | null,
  services: WidgetBootstrapResponse.ServiceItem[]
): string | null {
  if (!vehicle?.evServicesUnavailable) return null

  const quoted = new Set((vehicle.quotes ?? []).map((quote) => quote.serviceId))
  // A free MOT retest is quoted but nobody books it on its own, so it names nothing.
  const offered = buildJobTypes(services.filter((service) => quoted.has(service.id))).flatMap(
    (jobType) => jobType.bookable
  )
  if (offered.length === 0) return NO_EV_WORK

  const found = new Set(
    offered.map((service) =>
      service.motService ? 'mot' : (kindOf(service.category) ?? kindOf(service.name))
    )
  )
  const named = ORDER.filter((kind) => found.has(kind))
  return named.length === 0
    ? `${NO_EV_SERVICING} – some services are still available below.`
    : `${NO_EV_SERVICING} – ${listed(named)}`
}
